import { useTranslation } from 'react-i18next';
import { Language } from '@/features/graphql/generated/graphql';

/**
 * Hook to get a localized value from a LocalizedString object
 * @param value - The LocalizedString object, JSON string, or regular string
 * @returns The localized string value with fallback handling
 */
export function useLocalizedValue(
  value: Partial<Record<Language, string>> | string | null | undefined,
): string {
  const { i18n } = useTranslation();

  // Handle null/undefined values
  if (!value) {
    return '';
  }

  // If it's a string, try to parse as JSON first
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      // If parsed successfully and it's an object, treat as localized string
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        value = parsed as Record<Language, string>;
      } else {
        // If parsing succeeded but it's not an object, return the original string
        return value;
      }
    } catch {
      // If parsing fails, return as regular string
      return value as string;
    }
  }

  // At this point, value should be a Record<Language, string>
  if (typeof value !== 'object') {
    return String(value);
  }

  const currentLanguage = i18n.language as Language;
  const fallbackLanguages = [Language.En, Language.Sk];

  // Try current language first
  if (value[currentLanguage]) {
    return value[currentLanguage];
  }

  // Try fallback languages
  const fallbackLanguage = fallbackLanguages.find((lang) => value[lang]);
  if (fallbackLanguage && value[fallbackLanguage]) {
    return value[fallbackLanguage];
  }

  // Return first available value as last resort
  const firstAvailable = Object.values(value).find((val) => val);
  return firstAvailable || '';
}
